import { useCallback, useEffect, useState } from "react";
import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { v4 } from "uuid";
import { Loader, Trash2 } from "lucide-react";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { ImageUpload } from "@/shared/components/images/image-upload";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  parseRTKQueryError,
  parseRTKQueryErrorToSonner,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import type {
  AssetRefDTO,
  MonitorAdditionalDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import type {
  AdditionalImageReq,
  AdditionalLibraryImageReq,
} from "@/services/backend/qpm/monitors/types/monitor-commands";
import {
  useQpmMonitorAdditionalImageMutation,
  useQpmMonitorAdditionalLibraryImageMutation,
  useQpmMonitorDeleteAdditionalImageMutation,
  useQpmMonitorDeleteAdditionalLibraryImageMutation,
  useQpmMonitorShowImageQuery,
} from "@/services/backend/qpm/monitors/api/monitor-api";
import {
  useQpmCatalogItemDeleteAdditionalImageMutation,
  useQpmCatalogItemSetAdditionalImageMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import type { SetItemAdditionalImageReq } from "@/services/backend/qpm/catalog/types/catalog";

export type AdditionalImageMode =
  | { kind: "base"; monitorId: string }
  | { kind: "catalog"; catalogItemId: string }
  | { kind: "library"; additionalId: string };

export function MonitorAdditionalImageSlot({
  additional,
  mode,
  disabled,
  readOnly,
}: {
  additional: MonitorAdditionalDTO | undefined;
  mode: AdditionalImageMode | undefined;
  disabled: boolean;
  readOnly?: boolean;
}) {
  if (readOnly) {
    return (
      <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
        {t(
          "Vorlagen aus der Suche werden zunächst unverändert übernommen. Nach dem Hinzufügen kann der Zusatz in der Liste ausgewählt und bearbeitet werden.",
        )}
      </div>
    );
  }

  if (!additional || !mode) {
    return (
      <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
        {t(
          "Bild kann gespeichert werden, sobald der Zusatz gespeichert wurde.",
        )}
      </div>
    );
  }

  return (
    <AdditionalImageEditor
      additional={additional}
      mode={mode}
      disabled={disabled}
    />
  );
}

function AdditionalImageEditor({
  additional,
  mode,
  disabled,
}: {
  additional: MonitorAdditionalDTO;
  mode: AdditionalImageMode;
  disabled: boolean;
}) {
  const [baseUpload, baseUploadState] = useQpmMonitorAdditionalImageMutation();
  const [catalogUpload, catalogUploadState] =
    useQpmCatalogItemSetAdditionalImageMutation();
  const [libraryUpload, libraryUploadState] =
    useQpmMonitorAdditionalLibraryImageMutation();
  const [baseDelete, baseDeleteState] =
    useQpmMonitorDeleteAdditionalImageMutation();
  const [catalogDelete, catalogDeleteState] =
    useQpmCatalogItemDeleteAdditionalImageMutation();
  const [libraryDelete, libraryDeleteState] =
    useQpmMonitorDeleteAdditionalLibraryImageMutation();
  const [uploadedImage, setUploadedImage] = useState<AssetRefDTO | undefined>();
  const effectiveImage = uploadedImage ?? additional.image;
  const additionalImageFileId =
    effectiveImage?.fileUuid ?? effectiveImage?.fileId ?? "";

  useEffect(() => {
    setUploadedImage(undefined);
  }, [additional.id]);

  const {
    data: imageData,
    isLoading: isImageLoading,
    error: imageError,
  } = useQpmMonitorShowImageQuery(
    {
      fileId: additionalImageFileId,
      filePrefix: effectiveImage?.prefix ?? "",
    },
    {
      skip: !additionalImageFileId || !effectiveImage?.prefix,
    },
  );

  const uploadState =
    mode.kind === "base"
      ? baseUploadState
      : mode.kind === "catalog"
        ? catalogUploadState
        : libraryUploadState;
  const deleteState =
    mode.kind === "base"
      ? baseDeleteState
      : mode.kind === "catalog"
        ? catalogDeleteState
        : libraryDeleteState;

  useEffect(() => {
    if (deleteState.isSuccess) {
      setUploadedImage(undefined);
    }
  }, [deleteState.isSuccess]);

  const uploadFile = useCallback(
    async (file: File) => {
      if (disabled) {
        return;
      }
      try {
        if (mode.kind === "base") {
          const fileId = v4();
          const image = await baseUpload({
            monitorId: mode.monitorId,
            additionalId: additional.id,
            fileId,
            file,
          } satisfies AdditionalImageReq).unwrap();
          setUploadedImage(image);
          toast.success(t("Bild gespeichert"));
          return;
        }
        if (mode.kind === "library") {
          const fileId = v4();
          const image = await libraryUpload({
            additionalId: mode.additionalId,
            fileId,
            file,
          } satisfies AdditionalLibraryImageReq).unwrap();
          setUploadedImage(image);
          toast.success(t("Bild gespeichert"));
          return;
        }
        const fileId = v4();
        const catalogItem = await catalogUpload({
          catalogItemId: mode.catalogItemId,
          additionalId: additional.id,
          fileId,
          file,
        } satisfies SetItemAdditionalImageReq).unwrap();
        const image = catalogItem.snapshot.additionals?.find(
          (item) => item.id === additional.id,
        )?.image;
        setUploadedImage(image);
        toast.success(t("Bild gespeichert"));
      } catch (error) {
        toast(
          ...parseRTKQueryErrorToSonner(
            error as FetchBaseQueryError | SerializedError,
          ),
        );
      }
    },
    [additional.id, baseUpload, catalogUpload, disabled, libraryUpload, mode],
  );

  const { onTrigger: triggerBaseDeleteImage } =
    usePersistenceStateUpdateOnTrigger(
      {
        monitorId: mode.kind === "base" ? mode.monitorId : "",
        additionalId: additional.id,
      },
      baseDelete,
      baseDeleteState.isLoading,
      baseDeleteState.error,
      baseDeleteState.isSuccess,
      baseDeleteState.reset,
      { toastError: true, toastSuccess: true },
    );

  const { onTrigger: triggerCatalogDeleteImage } =
    usePersistenceStateUpdateOnTrigger(
      {
        catalogItemId: mode.kind === "catalog" ? mode.catalogItemId : "",
        additionalId: additional.id,
      },
      catalogDelete,
      catalogDeleteState.isLoading,
      catalogDeleteState.error,
      catalogDeleteState.isSuccess,
      catalogDeleteState.reset,
      { toastError: true, toastSuccess: true },
    );

  const { onTrigger: triggerLibraryDeleteImage } =
    usePersistenceStateUpdateOnTrigger(
      {
        additionalId: mode.kind === "library" ? mode.additionalId : "",
      },
      libraryDelete,
      libraryDeleteState.isLoading,
      libraryDeleteState.error,
      libraryDeleteState.isSuccess,
      libraryDeleteState.reset,
      { toastError: true, toastSuccess: true },
    );

  const triggerDeleteImage =
    mode.kind === "base"
      ? triggerBaseDeleteImage
      : mode.kind === "catalog"
        ? triggerCatalogDeleteImage
        : triggerLibraryDeleteImage;

  if (isImageLoading) {
    return (
      <div className="flex min-h-32 items-center justify-center rounded-md border">
        <Loader className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (imageError) {
    return <RTKQueryErrorAlert error={imageError} />;
  }

  return (
    <div className="space-y-2">
      <Label>{t("Bild für Katalog/PDF")}</Label>
      <div className="rounded-md border p-3">
        <ImageUpload
          id={`additional-image-${additional.id}`}
          showImagePreview
          existingImageUrl={imageData?.objectURL}
          onUpload={uploadFile}
          err={parseRTKQueryError(uploadState.error ?? deleteState.error)}
          disabled={disabled || uploadState.isLoading || deleteState.isLoading}
          wrapperClassName="min-h-56"
          imageClassName="max-h-40 object-contain"
          actions={
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={triggerDeleteImage}
                    disabled={
                      disabled ||
                      uploadState.isLoading ||
                      deleteState.isLoading ||
                      !effectiveImage
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Bild löschen")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          }
        />
      </div>
    </div>
  );
}
